/**
 * Analytics — redesigned (EM handoff): KPI strip, profitability-by-project
 * diverging bars, billable-mix donut, team utilization & cost table, plus a
 * Team Cost editor (admin). Wired to /api/workspace/analytics + /team-cost.
 */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "../../utils/toast";
import {
  Banknote,
  Wallet,
  TrendingUp,
  Percent,
  BarChart3,
  PieChart,
  Users,
  Save,
  Pencil,
} from "lucide-react";
import { wsApi, canManageCost } from "./wsApi";
import { T, RAW, Avatar } from "../../components/em/ui";
import { Pills, KpiCard, ProgressBar, Panel, money } from "./wsUi";

const isoDate = (d) => {
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};
const startOfMonth = () => {
  const d = new Date();
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
};
const today = () => isoDate(new Date());

export default function Analytics() {
  const [view, setView] = useState("reports");
  const [from, setFrom] = useState(startOfMonth());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    wsApi
      .analytics(`?${p.toString()}`)
      .then(setData)
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const projects = data?.projects || [];
  // revenue/cost arrive from SQL aggregates (strings) — coerce so these add
  // numerically rather than string-concatenating.
  const totalRev = projects.reduce((s, p) => s + (Number(p.revenue) || 0), 0);
  const totalCost = projects.reduce((s, p) => s + (Number(p.cost) || 0), 0);
  const totalProfit = totalRev - totalCost;
  const margin = totalRev ? Math.round((totalProfit / totalRev) * 100) : 0;
  const totals = data?.totals || {};
  const billH = totals.billable_hours || 0;
  const nonBillH = totals.nonbillable_hours || 0;
  const billPct = billH + nonBillH > 0 ? billH / (billH + nonBillH) : 0;

  const projRows = [...projects].sort(
    (a, b) => (b.profit || 0) - (a.profit || 0),
  );
  const maxAbs = Math.max(
    1,
    ...projRows.map((r) => Math.max(r.revenue || 0, r.cost || 0)),
  );

  const R = 52,
    C = 2 * Math.PI * R;

  const kpis = [
    {
      label: "Total Revenue",
      value: money(totalRev),
      Icon: Banknote,
      color: T.success600,
      bg: T.success50,
    },
    {
      label: "Labour Cost",
      value: money(totalCost),
      Icon: Wallet,
      color: T.danger,
      bg: T.danger50,
    },
    {
      label: "Net Profit",
      value: money(totalProfit),
      Icon: TrendingUp,
      color: T.orange,
      bg: T.orange50,
    },
    {
      label: "Blended Margin",
      value: `${margin}%`,
      Icon: Percent,
      color: T.purple,
      bg: T.purple50,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Pills
          options={[
            { value: "reports", label: "Reports" },
            { value: "cost", label: "Team Cost" },
          ]}
          value={view}
          onChange={setView}
        />
        {view === "reports" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={dateInput}
              />
              <span style={{ color: T.fg7 }}>→</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={dateInput}
              />
            </div>
          </>
        )}
      </div>

      {view === "reports" ? (
        loading ? (
          <div style={{ textAlign: "center", padding: 60, color: T.fg7 }}>
            Loading…
          </div>
        ) : (
          <>
            {/* KPI strip */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 14,
                marginBottom: 18,
              }}
            >
              {kpis.map((k) => (
                <KpiCard key={k.label} {...k} />
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) 320px",
                gap: 16,
                marginBottom: 16,
              }}
            >
              {/* Profitability by project */}
              <Panel padding="20px 22px" title={null}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 18,
                  }}
                >
                  <BarChart3 size={16} color={T.orange} />
                  <span style={{ font: `700 14px ${T.font}`, color: T.fg1 }}>
                    Profitability by project
                  </span>
                  <div style={{ flex: 1 }} />
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      font: `500 11px ${T.font}`,
                      color: T.fg5,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: T.gradient,
                      }}
                    />
                    Revenue
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      font: `500 11px ${T.font}`,
                      color: T.fg5,
                      marginLeft: 12,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: "#fecaca",
                      }}
                    />
                    Cost
                  </span>
                </div>
                {projRows.length === 0 ? (
                  <div
                    style={{
                      color: T.fg7,
                      font: `400 13px ${T.font}`,
                      padding: "12px 0",
                    }}
                  >
                    No project data in this range.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 16,
                    }}
                  >
                    {projRows.map((r) => (
                      <div key={r.project_id}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 3,
                              background: r.color || T.orange,
                            }}
                          />
                          <span
                            style={{
                              font: `600 12px ${T.font}`,
                              color: T.fg1,
                              marginLeft: 8,
                              flex: 1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {r.name}
                          </span>
                          <span
                            style={{
                              font: `700 12px ${T.font}`,
                              color: r.profit >= 0 ? T.success600 : T.danger,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {r.profit >= 0 ? "+" : ""}
                            {money(r.profit)}
                          </span>
                          <span
                            style={{
                              font: `500 11px ${T.font}`,
                              color: T.fg7,
                              marginLeft: 8,
                              width: 42,
                              textAlign: "right",
                            }}
                          >
                            {r.margin}%
                          </span>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                          }}
                        >
                          <div
                            style={{
                              height: 9,
                              borderRadius: 9999,
                              background: T.bgHead,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${(r.revenue / maxAbs) * 100}%`,
                                background: T.gradient,
                                borderRadius: 9999,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              height: 9,
                              borderRadius: 9999,
                              background: T.bgHead,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${(r.cost / maxAbs) * 100}%`,
                                background: "#fecaca",
                                borderRadius: 9999,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              {/* Billable donut */}
              <Panel
                padding="20px 22px"
                title={null}
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <PieChart size={16} color={T.success600} />
                  <span style={{ font: `700 14px ${T.font}`, color: T.fg1 }}>
                    Billable mix
                  </span>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    minHeight: 160,
                  }}
                >
                  <svg width="150" height="150" viewBox="0 0 130 130">
                    <circle
                      cx="65"
                      cy="65"
                      r={R}
                      fill="none"
                      stroke={RAW.bgMuted}
                      strokeWidth="16"
                    />
                    <circle
                      cx="65"
                      cy="65"
                      r={R}
                      fill="none"
                      stroke={RAW.success600}
                      strokeWidth="16"
                      strokeLinecap="round"
                      strokeDasharray={`${C * billPct} ${C}`}
                      transform="rotate(-90 65 65)"
                    />
                  </svg>
                  <div style={{ position: "absolute", textAlign: "center" }}>
                    <div
                      style={{
                        font: `800 26px ${T.font}`,
                        color: T.fg1,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {Math.round(billPct * 100)}%
                    </div>
                    <div style={{ font: `500 10px ${T.font}`, color: T.fg7 }}>
                      billable
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 3,
                        background: T.success600,
                      }}
                    />
                    <span
                      style={{
                        font: `500 12px ${T.font}`,
                        color: T.fg4,
                        flex: 1,
                      }}
                    >
                      Billable
                    </span>
                    <span style={{ font: `600 12px ${T.font}`, color: T.fg1 }}>
                      {billH}h
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 3,
                        background: T.border,
                      }}
                    />
                    <span
                      style={{
                        font: `500 12px ${T.font}`,
                        color: T.fg4,
                        flex: 1,
                      }}
                    >
                      Non-billable
                    </span>
                    <span style={{ font: `600 12px ${T.font}`, color: T.fg1 }}>
                      {nonBillH}h
                    </span>
                  </div>
                </div>
              </Panel>
            </div>

            {/* Utilization table */}
            <Panel padding="20px 22px" title={null}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 16,
                }}
              >
                <Users size={16} color={T.info600} />
                <span style={{ font: `700 14px ${T.font}`, color: T.fg1 }}>
                  Team utilization & cost
                </span>
              </div>
              {(data?.utilization || []).length === 0 ? (
                <div style={{ color: T.fg7, font: `400 13px ${T.font}` }}>
                  No time logged in this range.
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 2 }}
                >
                  {data.utilization.map((u) => {
                    // Capacity-based utilization (logged ÷ capacity for the
                    // range) when available; else fall back to a 160h month.
                    const capPct =
                      u.capacity_utilization != null
                        ? u.capacity_utilization
                        : Math.round((u.total_hours / 160) * 100);
                    const pct = Math.min(100, capPct);
                    return (
                      <div
                        key={u.user_id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "9px 0",
                          borderBottom: `1px solid ${T.divider}`,
                        }}
                      >
                        <Avatar name={u.username} size={28} />
                        <div style={{ width: 150, minWidth: 150 }}>
                          <div
                            style={{
                              font: `600 12px ${T.font}`,
                              color: T.fg1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {u.username}
                          </div>
                          <div
                            style={{ font: `400 10px ${T.font}`, color: T.fg7 }}
                          >
                            ₹{u.hourly_cost}/hr
                          </div>
                        </div>
                        <ProgressBar
                          pct={pct}
                          color={
                            pct > 85 ? T.danger : pct > 50 ? T.orange : T.info
                          }
                          height={8}
                        />
                        <span
                          style={{
                            font: `600 11px ${T.font}`,
                            color: T.fg4,
                            width: 92,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {u.total_hours}h
                          {u.capacity_utilization != null && (
                            <span
                              title={`${u.total_hours}h of ${u.capacity_hours}h capacity`}
                              style={{
                                color: capPct > 100 ? T.danger : capPct >= 85 ? T.orange : T.fg7,
                                fontWeight: 700,
                                marginLeft: 5,
                              }}
                            >
                              {capPct}%
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            font: `700 12px ${T.font}`,
                            color: T.fg1,
                            width: 80,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {money(u.cost)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </>
        )
      ) : (
        <TeamCost />
      )}
    </div>
  );
}

// ── Team Cost editor ────────────────────────────────────────────────────────
function TeamCost() {
  const editable = canManageCost();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [val, setVal] = useState("");
  const [capVal, setCapVal] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    wsApi
      .listTeamCost()
      .then(setRows)
      .catch(() => toast.error("Could not load team cost"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async (u) => {
    try {
      const hourly = val === "" ? 0 : Number(val);
      const capacity = capVal === "" ? 40 : Number(capVal);
      await wsApi.updateTeamCost(u.id, { hourly_cost: hourly, weekly_capacity_hours: capacity });
      setRows((r) =>
        r.map((x) =>
          x.id === u.id ? { ...x, hourly_cost: hourly, weekly_capacity_hours: capacity } : x,
        ),
      );
      setEditId(null);
      toast.success("Cost & capacity updated");
    } catch {
      toast.error("Could not update (admin only)");
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: 60, color: T.fg7 }}>
        Loading…
      </div>
    );

  return (
    <Panel padding="20px 22px" title="Employee Hourly Cost">
      <div
        style={{
          font: `400 12px ${T.font}`,
          color: T.fg7,
          margin: "-8px 0 14px",
        }}
      >
        {editable
          ? "Used to calculate project cost & profitability."
          : "Read-only — only an Admin can edit."}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {rows.map((u) => (
          <div
            key={u.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 0",
              borderBottom: `1px solid ${T.divider}`,
            }}
          >
            <Avatar name={u.username} size={30} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>
                {u.username}
              </div>
              <div style={{ font: `400 11px ${T.font}`, color: T.fg7 }}>
                {u.role}
                {u.department ? ` · ${u.department}` : ""}
              </div>
            </div>
            {editId === u.id ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  min="0"
                  autoFocus
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && save(u)}
                  title="Hourly cost (₹)"
                  style={{ ...dateInput, width: 90 }}
                />
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={capVal}
                  onChange={(e) => setCapVal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && save(u)}
                  title="Weekly capacity (hours)"
                  style={{ ...dateInput, width: 80 }}
                />
              </div>
            ) : (
              <span style={{ font: `700 13px ${T.font}`, color: T.fg1, textAlign: "right" }}>
                ₹{Number(u.hourly_cost).toLocaleString("en-IN")}
                <span style={{ color: T.fg7, fontWeight: 400, fontSize: 11 }}>/hr</span>
                <span style={{ color: T.fg7, fontWeight: 400, fontSize: 11 }}>
                  {" · "}{Number(u.weekly_capacity_hours ?? 40)}h/wk
                </span>
              </span>
            )}
            {editable &&
              (editId === u.id ? (
                <button onClick={() => save(u)} style={miniBtn(true)}>
                  <Save size={13} /> Save
                </button>
              ) : (
                <button
                  onClick={() => {
                    setEditId(u.id);
                    setVal(String(u.hourly_cost || ""));
                    setCapVal(String(u.weekly_capacity_hours ?? 40));
                  }}
                  style={miniBtn(false)}
                >
                  <Pencil size={12} /> Edit
                </button>
              ))}
          </div>
        ))}
      </div>
    </Panel>
  );
}

const dateInput = {
  border: `1px solid ${T.border}`,
  borderRadius: T.rMd,
  padding: "8px 10px",
  font: `500 13px ${T.font}`,
  color: T.fg1,
  outline: "none",
};
const miniBtn = (primary) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "6px 12px",
  borderRadius: 7,
  cursor: "pointer",
  font: `600 12px ${T.font}`,
  border: primary ? 0 : `1px solid ${T.border}`,
  background: primary ? T.gradient : T.bgCard,
  color: primary ? "#fff" : T.fg5,
});
